import { ImagePaths } from '../../lib/imageUrls.js';

export const entraIdHardeningIdentityProtection = {
  id: 56,
  slug: 'entra-id-hardening-identity-protection',
  ogImage: '/og/courses/entra-id-hardening-identity-protection.jpg',
  courseCode: 'ENTRA-HARD-001',
  featured: false,
  title: 'Entra ID Hardening — Identity Protection Deep-Dive',
  subtitle: 'Eliminate identity risk in Microsoft Entra ID with risk-based access, PIM, and Zero Trust controls',

  valueProposition: 'Identity is the new perimeter — and Microsoft Entra ID is the identity perimeter for most Australian organisations. This deep-dive programme goes beyond Conditional Access fundamentals into risk-based access, Entra ID Protection, Privileged Identity Management (PIM) at scale, lifecycle automation, and continuous access evaluation — building a hardened Entra ID posture aligned to ASD Essential Eight ML2 and the ACSC Information Security Manual identity control families.',

  duration: '24 hours (3 days intensive or 6 weeks part-time)',
  students: 'New',
  level: 'Advanced',
  image: ImagePaths.COURSE_3,
  shortDescription: 'ENTRA-HARD-001 — Entra ID hardening deep-dive covering Identity Protection risk policies, PIM at scale, lifecycle workflows, and Zero Trust controls for Australian identity engineers.',

  price: {
    aud: 1299,
    currency: 'AUD',
    earlyBird: 999
  },

  stripeConfig: {
    priceId: 'price_1U3WMqKu1nyc322UhI5jPSdh',
    productId: 'prod_V3deXdfD7JBicJ'
  },

  snapshot: {
    whoItsFor: [
      'Identity engineers who completed the Conditional Access Workshop and want depth beyond policy design',
      'Entra ID administrators implementing Privileged Identity Management for Essential Eight ML2',
      'Security architects hardening identity infrastructure before Copilot deployment',
      'IT managers accountable for identity security posture under ACSC ISM and privacy obligations'
    ],
    accreditation: 'Aligned to ASD Essential Eight Maturity Level 2 and 3 (MFA, Restrict Administrative Privileges) and ACSC Information Security Manual identity and access management control families'
  },

  logistics: {
    classSize: 'Maximum 10 participants — lab-intensive with individual Entra ID P2 access',
    requirements: [
      'Completion of Microsoft Entra Conditional Access Workshop (CA-WS-001) or equivalent operational CA experience',
      'Microsoft Entra ID P2 tenant access (Microsoft 365 E5 or standalone Entra ID P2)',
      'At least one year of Entra ID administration experience in a production environment',
      'Familiarity with PowerShell and Microsoft Graph is beneficial'
    ]
  },

  overview: 'The Conditional Access Workshop (CA-WS-001) covers policy design and zero-trust fundamentals. This Entra ID Hardening deep-dive takes engineers into the operational detail: how Entra ID Protection risk signals work and how to tune them for Australian organisations, how to implement Privileged Identity Management for every privileged role including Global Administrator, how to automate the identity lifecycle with Lifecycle Workflows so joiner-mover-leaver controls are consistent and auditable, and how Continuous Access Evaluation provides real-time token revocation for high-value sessions. The programme also addresses the specific hardening steps required before deploying Microsoft 365 Copilot — identity hygiene is the most frequently missed prerequisite in Australian Copilot deployments.',

  outcomes: [
    'Configure Entra ID Protection user risk and sign-in risk policies tuned to Australian organisational patterns',
    'Implement named location and travel anomaly policies using ACSC-aligned trusted IP ranges',
    'Deploy Privileged Identity Management (PIM) for all privileged roles: just-in-time activation, approval workflows, and access reviews',
    'Design break-glass accounts and emergency access procedures with audit alerting',
    'Automate joiner-mover-leaver lifecycle using Entra ID Lifecycle Workflows and HR-driven provisioning',
    'Configure Continuous Access Evaluation (CAE) for real-time token revocation on session compromise',
    'Implement Entra ID audit log export to Microsoft Sentinel for persistent identity monitoring',
    'Produce an identity hardening evidence pack aligned to ASD Essential Eight ML2 Restrict Administrative Privileges'
  ],

  audience: [
    'Identity engineers who have Conditional Access in production and need operational depth',
    'Entra ID administrators rolling out Privileged Identity Management for Essential Eight ML2',
    'Security architects hardening identity posture ahead of a Microsoft 365 Copilot deployment',
    'IT managers accountable for identity security evidence under the ACSC ISM and Privacy Act 1988',
    'Platform engineers automating joiner-mover-leaver provisioning from Workday or SAP SuccessFactors',
    'IRAP-adjacent assessors and internal auditors reviewing privileged access controls'
  ],

  prerequisites: [
    'Completion of the Microsoft Entra Conditional Access Workshop (CA-WS-001) or equivalent operational CA experience',
    'At least one year of Entra ID administration in a production environment',
    'Microsoft Entra ID P2 tenant access — Microsoft 365 E5 or standalone Entra ID P2 (P1 is insufficient)',
    'Global Administrator or Privileged Role Administrator rights in the lab tenant',
    'Familiarity with PowerShell and Microsoft Graph is beneficial but not required'
  ],

  modules: [
    {
      number: 1,
      title: 'Entra ID Protection — Risk-Based Access',
      duration: '6 hours',
      topics: [
        'Entra ID Protection risk signal architecture: sign-in risk, user risk, and the Microsoft threat intelligence feed',
        'Risk policy design: self-remediation vs. block vs. MFA step-up — tuning for false positive tolerance',
        'Named locations and trusted IP management for Australian offices, VPNs, and cloud egress points',
        'Risky user workflow: investigation, remediation, and dismissal procedures for SOC integration',
        'Continuous Access Evaluation: real-time token revocation for device compliance loss and location change'
      ]
    },
    {
      number: 2,
      title: 'Privileged Identity Management at Scale',
      duration: '6 hours',
      topics: [
        'PIM for Entra ID roles: scope, activation settings, approval workflows, and notification configuration',
        'PIM for Azure RBAC roles: resource-scoped just-in-time access for Azure subscription owners',
        'PIM access reviews: quarterly review cycles, auto-apply, and reviewer delegation for Essential Eight ML2 evidence',
        'Break-glass account design: naming conventions, MFA exclusion, monitoring alerts, and annual access test',
        'Privileged Access Workstations (PAW) and Secure Admin Workstation (SAW) concepts for Tier 0 admins'
      ]
    },
    {
      number: 3,
      title: 'Identity Lifecycle Automation',
      duration: '6 hours',
      topics: [
        'Entra ID Lifecycle Workflows: joiner, mover, and leaver workflow design and trigger configuration',
        'HR-driven provisioning from Workday and SAP SuccessFactors to Entra ID',
        'Entitlement Management: access packages, connected organisations, and auto-assignment for role lifecycle',
        'Guest user lifecycle: external collaboration policies, review cadence, and automated expiry',
        'Access review design: frequency, reviewer selection, and auto-apply for Privacy Act APP 11 obligations'
      ]
    },
    {
      number: 4,
      title: 'Advanced Hardening and Evidence Pack',
      duration: '6 hours',
      topics: [
        'Copilot identity prerequisites: sensitivity labels, guest access review, admin role reduction, and Secure Score targets',
        'Entra ID audit log export to Sentinel: diagnostic settings, retention, and identity threat analytics',
        'Microsoft Secure Score identity hardening: recommended actions mapped to Essential Eight ML2 and ML3',
        'Identity hardening evidence pack: PIM access reviews, CA policy exports, and risk policy configurations for IRAP',
        'Capstone lab: produce a complete ML2 identity hardening evidence document for an Australian SMB or enterprise scenario'
      ]
    }
  ],

  format: {
    type: 'Live online deep-dive — 3 days intensive or 6 weeks part-time, with individual Entra ID P2 lab access',
    includes: [
      '24 hours of lab-intensive instruction capped at 10 participants',
      'Individual Entra ID P2 lab tenant for risk policy, PIM and Lifecycle Workflow exercises',
      'Risk policy tuning guide for Australian sign-in patterns and named location design',
      'PIM rollout templates: activation settings, approval workflows and quarterly access review cycles',
      'Break-glass account design standard with monitoring alerts and annual access test procedure',
      'Copilot identity prerequisite checklist covering admin role reduction and guest access review',
      'Capstone: a complete Essential Eight ML2 identity hardening evidence document for IRAP use',
      'Recorded sessions for 6 months and a certificate of completion'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Three-day intensive cohorts run every six weeks; the 6-week part-time stream runs one half-day per week. Commonly booked as the follow-on to the Conditional Access Workshop'
  },

  certification: 'No vendor exam is directly associated with this workshop. Course content covers SC-300 and SC-100 identity governance domains at operational depth beyond exam requirements. Participants receive an EDUC4TE certificate of completion.',

  faqs: [
    {
      question: 'What is the difference between the Conditional Access Workshop and this course?',
      answer: 'The Conditional Access Workshop (CA-WS-001) is the right starting point for administrators new to Conditional Access policy design. This Entra ID Hardening deep-dive assumes CA fundamentals are in place and focuses on the harder operational problems: risk policy tuning, PIM at scale, lifecycle automation, and continuous access evaluation. The two courses are designed as a sequence.'
    },
    {
      question: 'Is Entra ID P2 required throughout, or can participants use P1?',
      answer: 'Entra ID P2 (included in Microsoft 365 E5 or available as a standalone add-on) is required for Entra ID Protection risk policies, Privileged Identity Management, and Lifecycle Workflows — the three core components of this course. P1 is insufficient. A 90-day Microsoft 365 E5 trial is the most practical lab option for participants who do not already have P2.'
    },
    {
      question: 'What does "Copilot identity prerequisites" mean in Module 4?',
      answer: 'Microsoft 365 Copilot queries every SharePoint site, Teams channel, and OneDrive folder the signed-in user has read access to. In an ungoverned Entra ID environment, this means Copilot will surface data the user should not see. The Module 4 Copilot prerequisites section covers the specific Entra ID hardening steps — admin role reduction, guest account review, oversharing detection — that must be done before enabling Copilot at scale.'
    },
    {
      question: 'Does this course cover Entra External ID (formerly Azure AD B2C)?',
      answer: 'No. This course focuses on Entra ID for workforce identity — employees, contractors, and privileged administrators. Entra External ID for customer-facing identity scenarios is a separate product family. Module 3 covers guest user lifecycle under B2B collaboration, which is within scope.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'Privacy Act 1988', 'OAIC APPs']
};
